// SETTINGS V2 — Team page pure utils (SETTINGS_V2_SP6 §5.5 / §5.6).
//
// getTimeRemainingV2 ports the legacy useEmployeeTime math VERBATIM (hook lines
// 4-31) but localizes the suffix (binds settings:team.remaining — the ticket
// §4.5 'remaining' FIX+FLAG); the legacy hook stays untouched. formatDate /
// formatDateTime format in the active UI locale via moment "L" (USER-LANG-IT-PL-1
// L8: IT needs DD/MM/YYYY); output for sk/cs/de/pl/en is unchanged (DD.MM.YYYY).
// No side effects.
import type { TFunction } from "i18next";
import moment from "moment";

export interface TimeRemainingV2 {
  expired: boolean;
  text: string;
}

/** Legacy math verbatim; only the hardcoded "remaining" suffix becomes i18n. */
export const getTimeRemainingV2 = (
  expirationDate: Date | undefined,
  t: TFunction
): TimeRemainingV2 | null => {
  if (!expirationDate) {
    return null;
  }

  const now = new Date();
  const expiration = new Date(expirationDate);
  const diffMs = expiration.getTime() - now.getTime();

  if (diffMs <= 0) {
    return { expired: true, text: "" };
  }

  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  let timeText = "";
  if (hours > 0) {
    timeText += `${hours}h `;
  }
  if (minutes > 0 || hours === 0) {
    timeText += `${minutes}m `;
  }

  // ticket §4.5 fix: localized suffix instead of the hook's hardcoded "remaining".
  return {
    expired: false,
    text: t("settings:team.remaining", { time: timeText.trim() }) as string,
  };
};

const pad = (n: number): string => String(n).padStart(2, "0");

/** Short date in the active UI locale — moment "L" (global locale set in core/i18n.ts):
 *  it DD/MM/YYYY, sk/cs/de/pl DD.MM.YYYY. Plain "en" is moment's US MM/DD/YYYY, so it
 *  keeps the previous DD.MM.YYYY. */
export const formatDate = (d: Date | string): string =>
  moment(new Date(d)).format(moment.locale() === "en" ? "DD.MM.YYYY" : "L");

/** formatDate + HH:mm. */
export const formatDateTime = (d: Date | string): string => {
  const date = new Date(d);
  return `${formatDate(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
